'use client';

import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import ReactAsyncSelect from 'react-select/async';
import { CustomOption } from './custom-components/custom-option';
import { CustomMenu } from './custom-components/custom-menu';
import { CustomDropdownIndicator } from './custom-components/custom-dropdown-indicator';
import { CustomClearIndicator } from './custom-components/custom-clear-indicator';
import { createSelectStyles } from './select-styles';

import type { TMenuPlacement, TOption } from './types';

/**
 * Searchable select whose options are loaded on demand.
 *
 * Same look, label markup and custom components as `Select` — it shares the
 * styling module rather than restyling react-select a second time. Use it where
 * the option set is too large to ship up front, such as the auction make
 * catalogue (2,849 entries) and its per-make models.
 */

type TAsyncSelect = {
  id: string;
  label: string;
  value?: TOption | null;
  onChange?: (option: TOption | null) => void;
  /** Called as the visitor types; return the options to show. */
  loadOptions: (inputValue: string) => Promise<TOption[]>;
  /** Options shown before anything is typed. */
  defaultOptions?: TOption[] | boolean;
  required?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  noOptionsMessage?: string;
  loadingMessage?: string;
  blacked?: boolean;
  /** Applied to the field wrapper, for grid placement by the caller. */
  className?: string;
  /** See `TSelect.menuPlacement`. */
  menuPlacement?: TMenuPlacement;
};

export const AsyncSelect: FC<TAsyncSelect> = ({
  id,
  label,
  value,
  onChange,
  loadOptions,
  defaultOptions = true,
  required,
  placeholder = 'Zacznij pisać...',
  isDisabled = false,
  noOptionsMessage = 'Brak wyników',
  loadingMessage = 'Szukam...',
  blacked = false,
  className,
  menuPlacement = 'bottom',
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className={twMerge('flex flex-col text-foreground gap-1.5', className)}>
      <label
        htmlFor={id}
        className={twMerge('tracking-wider font-semibold text-sm pl-1', blacked && 'text-background')}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
      </label>
      <ReactAsyncSelect
        styles={createSelectStyles({ blacked, isHovered })}
        components={{
          Option: CustomOption,
          Menu: CustomMenu,
          DropdownIndicator: CustomDropdownIndicator,
          ClearIndicator: CustomClearIndicator,
        }}
        cacheOptions
        defaultOptions={defaultOptions}
        loadOptions={loadOptions}
        name={id}
        inputId={id}
        instanceId={id}
        aria-label={label}
        isDisabled={isDisabled}
        onChange={(option: unknown) => onChange?.((option as TOption | null) ?? null)}
        value={value ?? null}
        placeholder={placeholder}
        noOptionsMessage={() => noOptionsMessage}
        loadingMessage={() => loadingMessage}
        menuPlacement={menuPlacement}
        openMenuOnFocus
        isClearable
      />
    </div>
  );
};
